import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TableCell,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const COPPER = "#B87333";
const COPPER_DARK = "#8A5523";
const COPPER_BORDER = "rgba(184, 115, 51, 0.22)";

function parseDetails(details) {
  if (!details) return { raw: "", parsed: null, formatted: "" };
  const raw = String(details);
  try {
    const parsed = JSON.parse(raw);
    return {
      raw,
      parsed,
      formatted: JSON.stringify(parsed, null, 2),
    };
  } catch {
    return { raw, parsed: null, formatted: raw };
  }
}

function firstUsefulValue(parsed) {
  if (!parsed || typeof parsed !== "object") return "";
  const priority = ["message", "input", "output", "error", "reason", "detail"];
  for (const key of priority) {
    const value = parsed[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  const entry = Object.entries(parsed).find(([, value]) => {
    return ["string", "number", "boolean"].includes(typeof value);
  });
  return entry ? String(entry[1]) : "";
}

function summarizeDetails(details) {
  const { raw, parsed, formatted } = parseDetails(details);
  const source = firstUsefulValue(parsed) || formatted || raw;
  const clean = String(source).replace(/\s+/g, " ").trim();
  if (!clean) return "No details";
  return clean.length > 145 ? `${clean.slice(0, 145).trim()}...` : clean;
}

export const DetailsCell = ({ details }) => {
  const [open, setOpen] = useState(false);
  const parsedDetails = useMemo(() => parseDetails(details), [details]);
  const summary = useMemo(() => summarizeDetails(details), [details]);
  const hasDetails = Boolean(parsedDetails.formatted);

  return (
    <TableCell>
      <Stack spacing={0.75} alignItems="flex-start">
        <Typography
          variant="body2"
          sx={{
            color: hasDetails ? "#1F2937" : "text.secondary",
            lineHeight: 1.5,
            overflowWrap: "anywhere",
          }}
        >
          {summary}
        </Typography>
        {hasDetails && (
          <Button
            variant="text"
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              setOpen(true);
            }}
            sx={{
              minWidth: 0,
              p: 0,
              color: COPPER_DARK,
              fontWeight: 800,
              textTransform: "none",
              "&:hover": {
                bgcolor: "transparent",
                color: COPPER,
                textDecoration: "underline",
                textUnderlineOffset: "3px",
              },
            }}
          >
            View details
          </Button>
        )}
      </Stack>

      <Dialog
        onClose={() => setOpen(false)}
        open={open}
        maxWidth="md"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 2,
            border: `1px solid ${COPPER_BORDER}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 2,
            pr: 6,
            borderBottom: `1px solid ${COPPER_BORDER}`,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Audit Event Details
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.25 }}>
            Full payload captured for this event.
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => setOpen(false)}
            sx={{
              position: "absolute",
              right: 10,
              top: 10,
              color: "text.secondary",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              maxHeight: "62vh",
              overflow: "auto",
              bgcolor: "rgba(15, 23, 42, 0.035)",
              color: "#111827",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: "0.84rem",
              lineHeight: 1.7,
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
            }}
          >
            {parsedDetails.formatted || "No details"}
          </Box>
        </DialogContent>
      </Dialog>
    </TableCell>
  );
};
